import React, { useEffect, useMemo, useState } from "react";
import {
    AutoComplete,
    Button,
    Card,
    Divider,
    Form,
    Input,
    InputNumber,
    Select,
    Space,
    Typography,
    message,
    Tag,
} from "antd";
import Loader from "../../components/loader/Loader";
import {
    GetV2AutomationSteps,
    ReplaceV2AutomationSteps,
    V2AutomationEdgeDto,
    V2AutomationStepDto,
    GetV2TemplatesContextList,
    V2TemplateListItemDto,
} from "../../api/v2/automations";

const { Text, Title } = Typography;

type Props = {
    domain: string;
    automationId: number;
};

type StepType = "wait" | "condition" | "wait_event" | "action";

const CONTEXT_SUGGESTIONS: string[] = [
    "context.title",
    "context.subtitle",
    "context.description",
    "context.callback_url",
    "context.callback_token",
    "context.image_url",
    "context.image_token",
    "context.images_url",
    "context.images_token",
    "context.buttons.0.label",
    "context.buttons.0.url",
    "context.buttons.0.token",
    "context.buttons.1.label",
    "context.buttons.1.url",
    "context.buttons.1.token",
    "context.buttons.2.label",
    "context.buttons.2.url",
    "context.buttons.2.token",
];

function parseJsonMaybe(v: any) {
    if (v == null) return null;
    if (typeof v === "string") {
        try {
            return JSON.parse(v);
        } catch {
            return null;
        }
    }
    return v;
}

function parseVariablesSchemaJson(raw: any): string[] {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.filter((x) => typeof x === "string");
    if (typeof raw === "string") {
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) return parsed.filter((x) => typeof x === "string");
        } catch {
            return [];
        }
    }
    return [];
}

function isSelectableWhatsApp(t: V2TemplateListItemDto) {
    const status = String(t.status || "").toLowerCase();
    const isReady = status === "ready";
    const contentTypes = parseJsonMaybe(t.content_types_json);
    const types = Array.isArray(contentTypes) ? contentTypes : [];
    const isMenu = types.includes("twilio/list-picker");
    const hasContent = !!t.provider_content_sid;
    return t.channel === "whatsapp" && (status === "approved" || isReady || (isMenu && hasContent));
}

function buildDefaultStep(stepType: StepType, stepIndex: number): V2AutomationStepDto {
    if (stepType === "wait") {
        return { step_index: stepIndex, step_type: stepType, config: { delay_seconds: 60 } };
    }
    if (stepType === "condition") {
        return { step_index: stepIndex, step_type: stepType, config: { condition_id: null } };
    }
    if (stepType === "wait_event") {
        return {
            step_index: stepIndex,
            step_type: stepType,
            config: { event_name: "", poll_seconds: 30, timeout_seconds: 3600 },
        };
    }
    if (stepType === "action") {
        return {
            step_index: stepIndex,
            step_type: stepType,
            config: { action_type: "send_whatsapp_template", template_id: null, variables_mapping: {} },
        };
    }
    return {
        step_index: stepIndex,
        step_type: "action",
        config: { action_type: "send_whatsapp_template", template_id: null, variables_mapping: {} },
    };
}

export default function StepsEditor({ domain, automationId }: Props) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [steps, setSteps] = useState<V2AutomationStepDto[]>([]);
    const [edges, setEdges] = useState<V2AutomationEdgeDto[]>([]);
    const [newType, setNewType] = useState<StepType>("wait");
    const [templatesLoading, setTemplatesLoading] = useState(true);
    const [templates, setTemplates] = useState<V2TemplateListItemDto[]>([]);

    useEffect(() => {
        let cancelled = false;
        const loadAll = async () => {
            setLoading(true);
            setTemplatesLoading(true);
            const [stepsRes, templatesRes] = await Promise.all([
                GetV2AutomationSteps(domain, automationId),
                GetV2TemplatesContextList(domain),
            ]);
            if (cancelled) return;

            if (!stepsRes.ok) {
                message.error(stepsRes.error || "Failed loading steps");
                setSteps([]);
                setEdges([]);
            } else {
                const loadedSteps: V2AutomationStepDto[] = (stepsRes.data?.steps || [])
                    .map((s: V2AutomationStepDto) => {
                        if (s.step_type === "send_whatsapp_template") {
                            return {
                                step_index: s.step_index,
                                step_type: "action",
                                config: {
                                    action_type: "send_whatsapp_template",
                                    ...(s.config || {}),
                                },
                            };
                        }
                        return s;
                    })
                    .slice()
                    .sort((a: V2AutomationStepDto, b: V2AutomationStepDto) => a.step_index - b.step_index);
                const loadedEdges = (stepsRes.data?.edges || []).slice();
                setSteps(loadedSteps);
                setEdges(loadedEdges);
            }

            if (!templatesRes.ok) {
                message.error(templatesRes.error || "Failed loading templates");
                setTemplates([]);
            } else {
                setTemplates(templatesRes.data?.templates || []);
            }

            setLoading(false);
            setTemplatesLoading(false);
        };

        loadAll();
        return () => {
            cancelled = true;
        };
    }, [domain, automationId]);

    const templatesById = useMemo(() => {
        const m = new Map<number, V2TemplateListItemDto>();
        for (const t of templates) m.set(t.id, t);
        return m;
    }, [templates]);

    const selectableTemplates = useMemo(() => templates.filter(isSelectableWhatsApp), [templates]);
    const stepIndexes = useMemo(() => steps.map((s) => Number(s.step_index)), [steps]);
    const stepIndexSet = useMemo(() => new Set(stepIndexes), [stepIndexes]);

    const addStep = () => {
        const maxIndex = steps.length ? Math.max(...steps.map((s) => Number(s.step_index))) : -1;
        const next = buildDefaultStep(newType, maxIndex + 1);
        setSteps([...steps, next].sort((a, b) => a.step_index - b.step_index));
    };

    const removeStep = (stepIndex: number) => {
        setSteps((prev) => prev.filter((s) => Number(s.step_index) !== stepIndex));
        setEdges((prev) =>
            prev.filter((e) => Number(e.from_step_index) !== stepIndex && Number(e.to_step_index) !== stepIndex)
        );
    };

    const updateStep = (stepIndex: number, patch: Partial<V2AutomationStepDto>) => {
        setSteps((prev) =>
            prev
                .map((s) => (Number(s.step_index) === stepIndex ? { ...s, ...patch } : s))
                .sort((a, b) => a.step_index - b.step_index)
        );
    };

    const updateStepType = (stepIndex: number, stepType: StepType) => {
        updateStep(stepIndex, buildDefaultStep(stepType, stepIndex));
    };

    const updateStepConfig = (stepIndex: number, configPatch: any) => {
        setSteps((prev) =>
            prev.map((s) => {
                if (Number(s.step_index) !== stepIndex) return s;
                return { ...s, config: { ...(s.config || {}), ...(configPatch || {}) } };
            })
        );
    };

    const updateTemplateId = (stepIndex: number, templateId: number | null) => {
        const t = templateId ? templatesById.get(templateId) : null;
        const schema = t ? parseVariablesSchemaJson((t as any).variables_schema_json) : [];
        const step = steps.find((s) => Number(s.step_index) === stepIndex);
        const prevMapping: Record<string, string> = { ...(step?.config?.variables_mapping || {}) };
        for (const key of schema) if (!prevMapping[key]) prevMapping[key] = key;
        updateStepConfig(stepIndex, { template_id: templateId ?? null, variables_mapping: prevMapping });
    };

    const updateVariableMapping = (stepIndex: number, fromVar: string, toPath: string) => {
        const step = steps.find((s) => Number(s.step_index) === stepIndex);
        const mapping: Record<string, string> = { ...(step?.config?.variables_mapping || {}) };
        const val = String(toPath || "").trim();
        if (!val) delete mapping[fromVar];
        else mapping[fromVar] = val;
        updateStepConfig(stepIndex, { variables_mapping: mapping });
    };

    const getOutgoing = (fromStepIndex: number) =>
        edges
            .filter((e) => Number(e.from_step_index) === fromStepIndex)
            .slice()
            .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));

    const setOutgoing = (fromStepIndex: number, nextOutgoing: V2AutomationEdgeDto[]) => {
        const normalized = nextOutgoing.map((e, i) => ({
            ...e,
            from_step_index: fromStepIndex,
            sort_order: i,
        }));
        setEdges((prev) => [...prev.filter((e) => Number(e.from_step_index) !== fromStepIndex), ...normalized]);
    };

    const addOutgoing = (fromStepIndex: number) => {
        const targets = stepIndexes.filter((i) => i !== fromStepIndex);
        if (!targets.length) return;
        const current = getOutgoing(fromStepIndex);
        const nextKey = current.length ? `edge_${current.length + 1}` : "default";
        setOutgoing(fromStepIndex, [
            ...current,
            {
                from_step_index: fromStepIndex,
                to_step_index: targets[0],
                edge_key: nextKey,
                sort_order: current.length,
            },
        ]);
    };

    const updateOutgoingRow = (
        fromStepIndex: number,
        rowIndex: number,
        patch: Partial<V2AutomationEdgeDto>
    ) => {
        const current = getOutgoing(fromStepIndex);
        if (!current[rowIndex]) return;
        current[rowIndex] = { ...current[rowIndex], ...patch };
        setOutgoing(fromStepIndex, current);
    };

    const removeOutgoingRow = (fromStepIndex: number, rowIndex: number) => {
        const current = getOutgoing(fromStepIndex);
        setOutgoing(
            fromStepIndex,
            current.filter((_, idx) => idx !== rowIndex)
        );
    };

    const save = async () => {
        setSaving(true);
        try {
            const payloadSteps = [...steps].sort((a, b) => Number(a.step_index) - Number(b.step_index));
            if (!payloadSteps.length) {
                message.error("Add at least one step");
                return;
            }
            if (!payloadSteps.some((s) => Number(s.step_index) === 0)) {
                message.error("Step index 0 is required");
                return;
            }

            for (const s of payloadSteps) {
                if (s.step_type === "wait") {
                    const secs = Number(s.config?.delay_seconds ?? 0);
                    if (!Number.isFinite(secs) || secs < 0) {
                        message.error(`Step ${s.step_index}: wait delay must be >= 0`);
                        return;
                    }
                }

                if (s.step_type === "wait_event") {
                    const eventName = String(s.config?.event_name || "").trim();
                    if (!eventName) {
                        message.error(`Step ${s.step_index}: wait_event requires event_name`);
                        return;
                    }
                }

                if (s.step_type === "condition") {
                    const conditionId = Number(s.config?.condition_id || 0);
                    if (!conditionId) {
                        message.error(`Step ${s.step_index}: condition requires condition_id`);
                        return;
                    }
                }

                if (s.step_type === "action") {
                    const actionType = String(s.config?.action_type || "send_whatsapp_template");
                    if (actionType !== "send_whatsapp_template") {
                        message.error(`Step ${s.step_index}: only send_whatsapp_template is executable right now`);
                        return;
                    }
                    if (!Number(s.config?.template_id || 0)) {
                        message.error(`Step ${s.step_index}: template is required`);
                        return;
                    }
                }
            }

            const payloadEdges = edges
                .filter((e) => stepIndexSet.has(Number(e.from_step_index)) && stepIndexSet.has(Number(e.to_step_index)))
                .map((e) => ({
                    from_step_index: Number(e.from_step_index),
                    to_step_index: Number(e.to_step_index),
                    edge_key: String(e.edge_key || "").trim(),
                    sort_order: Number(e.sort_order || 0),
                }));

            for (const e of payloadEdges) {
                if (!e.edge_key) {
                    message.error("Each edge requires edge_key");
                    return;
                }
            }

            const res = await ReplaceV2AutomationSteps(domain, automationId, payloadSteps, payloadEdges);
            if (!res.ok) {
                message.error(res.error || "Failed saving steps");
                return;
            }

            message.success("Steps saved");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                <Loader size={56} isFullPage={false} />
            </div>
        );
    }

    return (
        <Card>
            <Title level={5} style={{ marginTop: 0 }}>
                Steps
            </Title>

            <Divider />

            <Space style={{ marginBottom: 12 }}>
                <Select
                    value={newType}
                    style={{ width: 280 }}
                    onChange={(v) => setNewType(v as StepType)}
                    options={[
                        { value: "wait", label: "Stand By (time delay)" },
                        { value: "condition", label: "Condition Branch" },
                        { value: "wait_event", label: "Wait Event Branch" },
                        { value: "action", label: "Action Node" },
                    ]}
                />
                <Button onClick={addStep}>Add node</Button>
                <Button type="primary" onClick={save} loading={saving} disabled={!steps.length}>
                    Save
                </Button>
            </Space>

            {!steps.length ? (
                <Text type="secondary">No steps yet. Add your first node above.</Text>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {steps.map((s) => {
                        const stepIndex = Number(s.step_index);
                        const templateId = Number(s.config?.template_id || 0) || null;
                        const selectedTemplate = templateId ? templatesById.get(templateId) : null;
                        const schema = selectedTemplate
                            ? parseVariablesSchemaJson((selectedTemplate as any).variables_schema_json)
                            : [];
                        const mapping: Record<string, string> = (s.config?.variables_mapping || {}) as any;
                        const missing = schema.filter((v) => !String(mapping?.[v] || "").trim());
                        const outgoing = getOutgoing(stepIndex);

                        return (
                            <Card key={stepIndex} size="small">
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                                    <Space>
                                        <Tag color="blue">Step {stepIndex}</Tag>
                                        <Select
                                            value={s.step_type}
                                            style={{ width: 280 }}
                                            onChange={(v) => updateStepType(stepIndex, v as StepType)}
                                            options={[
                                                { value: "wait", label: "Stand By (time delay)" },
                                                { value: "condition", label: "Condition Branch" },
                                                { value: "wait_event", label: "Wait Event Branch" },
                                                { value: "action", label: "Action Node" },
                                            ]}
                                        />
                                    </Space>

                                    <Button danger onClick={() => removeStep(stepIndex)}>
                                        Remove
                                    </Button>
                                </div>

                                <Divider style={{ margin: "12px 0" }} />

                                {s.step_type === "wait" ? (
                                    <Form layout="vertical">
                                        <Form.Item label="Delay (seconds)">
                                            <InputNumber
                                                min={0}
                                                style={{ width: 240 }}
                                                value={Number(s.config?.delay_seconds ?? 0)}
                                                onChange={(v) => updateStepConfig(stepIndex, { delay_seconds: Number(v || 0) })}
                                            />
                                        </Form.Item>
                                    </Form>
                                ) : null}

                                {s.step_type === "wait_event" ? (
                                    <Form layout="vertical">
                                        <Form.Item label="Event name to wait for">
                                            <Input
                                                value={String(s.config?.event_name || "")}
                                                onChange={(e) => updateStepConfig(stepIndex, { event_name: e.target.value })}
                                                placeholder="e.g. button.click"
                                            />
                                        </Form.Item>
                                        <Form.Item label="Polling interval (seconds)">
                                            <InputNumber
                                                min={5}
                                                style={{ width: 240 }}
                                                value={Number(s.config?.poll_seconds ?? 30)}
                                                onChange={(v) => updateStepConfig(stepIndex, { poll_seconds: Number(v || 30) })}
                                            />
                                        </Form.Item>
                                        <Form.Item label="Timeout (seconds, 0 = never timeout)">
                                            <InputNumber
                                                min={0}
                                                style={{ width: 240 }}
                                                value={Number(s.config?.timeout_seconds ?? 0)}
                                                onChange={(v) => updateStepConfig(stepIndex, { timeout_seconds: Number(v || 0) })}
                                            />
                                        </Form.Item>
                                    </Form>
                                ) : null}

                                {s.step_type === "condition" ? (
                                    <Form layout="vertical">
                                        <Form.Item label="Condition ID">
                                            <InputNumber
                                                min={1}
                                                style={{ width: 260 }}
                                                value={Number(s.config?.condition_id || 0) || undefined}
                                                onChange={(v) => updateStepConfig(stepIndex, { condition_id: Number(v || 0) || null })}
                                            />
                                        </Form.Item>
                                        <Text type="secondary">Use edge keys `true` / `false` for branch outputs.</Text>
                                    </Form>
                                ) : null}

                                {s.step_type === "action" ? (
                                    <Form layout="vertical">
                                        <Form.Item label="Action type">
                                            <Select
                                                style={{ width: 320 }}
                                                value={String(s.config?.action_type || "send_whatsapp_template")}
                                                options={[
                                                    { value: "send_whatsapp_template", label: "Send WhatsApp Template" },
                                                    { value: "webhook", label: "Webhook (coming soon)" },
                                                    { value: "tag_client", label: "Tag Client (coming soon)" },
                                                    { value: "set_property", label: "Set Property (coming soon)" },
                                                ]}
                                                onChange={(v) => updateStepConfig(stepIndex, { action_type: v })}
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            label={
                                                <Space>
                                                    <span>WhatsApp template</span>
                                                    {templatesLoading ? <Tag>Loading…</Tag> : null}
                                                    <Tag color="blue">approved/ready only</Tag>
                                                </Space>
                                            }
                                        >
                                            <Select
                                                showSearch
                                                placeholder="Select approved/ready WhatsApp template"
                                                style={{ width: 520, maxWidth: "100%" }}
                                                value={s.config?.template_id ?? null}
                                                onChange={(v) => updateTemplateId(stepIndex, v as number)}
                                                optionFilterProp="label"
                                                options={selectableTemplates.map((t) => ({
                                                    value: t.id,
                                                    label: `${t.title} (#${t.id})`,
                                                }))}
                                                filterOption={(input, opt) =>
                                                    String(opt?.label || "")
                                                        .toLowerCase()
                                                        .includes(String(input || "").toLowerCase())
                                                }
                                            />
                                        </Form.Item>

                                        {schema.length ? (
                                            <div style={{ marginTop: 8 }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                                                    <Text strong>Variables mapping</Text>
                                                    {missing.length ? <Tag color="red">{missing.length} unmapped</Tag> : <Tag color="green">all mapped</Tag>}
                                                </div>

                                                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                                    {schema.map((fromVar) => (
                                                        <div key={fromVar} style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                                                            <div style={{ width: 320, maxWidth: "100%" }}>
                                                                <Tag style={{ fontFamily: "monospace" }}>{fromVar}</Tag>
                                                            </div>
                                                            <AutoComplete
                                                                style={{ width: 520, maxWidth: "100%" }}
                                                                placeholder="Select or type a context path"
                                                                options={CONTEXT_SUGGESTIONS.map((v) => ({ value: v }))}
                                                                value={mapping?.[fromVar] || ""}
                                                                onChange={(v) => updateVariableMapping(stepIndex, fromVar, String(v || ""))}
                                                                filterOption={(inputValue, option) =>
                                                                    String(option?.value || "")
                                                                        .toLowerCase()
                                                                        .includes(String(inputValue || "").toLowerCase())
                                                                }
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : null}
                                    </Form>
                                ) : null}

                                <Divider style={{ margin: "12px 0" }} />
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                                    <Text strong>Outgoing edges</Text>
                                    <Button onClick={() => addOutgoing(stepIndex)} size="small">
                                        Add edge
                                    </Button>
                                </div>

                                {!outgoing.length ? (
                                    <Text type="secondary">No outgoing edges. Run ends here.</Text>
                                ) : (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                        {outgoing.map((edge, rowIdx) => (
                                            <Space key={`${stepIndex}-${rowIdx}`} wrap>
                                                <Input
                                                    style={{ width: 180 }}
                                                    value={String(edge.edge_key || "")}
                                                    onChange={(e) => updateOutgoingRow(stepIndex, rowIdx, { edge_key: e.target.value })}
                                                    placeholder="edge key (default/true/false...)"
                                                />
                                                <Select
                                                    style={{ width: 200 }}
                                                    value={Number(edge.to_step_index)}
                                                    options={stepIndexes
                                                        .filter((i) => i !== stepIndex)
                                                        .map((i) => ({ value: i, label: `Step ${i}` }))}
                                                    onChange={(v) => updateOutgoingRow(stepIndex, rowIdx, { to_step_index: Number(v) })}
                                                />
                                                <Button danger onClick={() => removeOutgoingRow(stepIndex, rowIdx)}>
                                                    Remove
                                                </Button>
                                            </Space>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        );
                    })}
                </div>
            )}
        </Card>
    );
}
